import * as React from 'react'
import { useCallback, useMemo } from 'react'
import { Field, Formik } from 'formik'
import { useAppDispatch } from '@user/store/hooks/useAppDispatch'
import { updateNotification } from '@user/store/notifications/api'
import { INotification } from '@user/store/notifications/typings'
import { SideDrawerForm } from '@sales-center/components'
import { NotificationLabel } from '../../atoms'

import Box from '@mui/material/Box'
import Divider from '@mui/material/Divider'
import { BaseTextField } from '@user/components/Formik'
import { FormModel } from './FormModel'
import { validate } from './validate'
import Typography from '@mui/material/Typography'
import MenuItem from '@mui/material/MenuItem'
import TextField from '@mui/material/TextField'
import RadioGroup from '@mui/material/RadioGroup'
import FormControlLabel from '@mui/material/FormControlLabel'
import Radio from '@mui/material/Radio'
import { numberOfReminders } from './constants'

type Props = {
  notification: INotification
  onClose: () => void
}

export function ManageAutoReminderSlideout(props: Props) {
  const { notification, onClose } = props

  const dispatch = useAppDispatch()

  const initialValues: FormModel = useMemo(
    () => ({
      maxReminderFollowups: notification.maxReminderFollowups,
      reminderFrequency: notification.reminderFrequency,
      reminderFrequencyType: notification.reminderFrequencyType,
    }),
    [
      notification.maxReminderFollowups,
      notification.reminderFrequency,
      notification.reminderFrequencyType,
    ],
  )

  const onSubmit = useCallback(
    async (model: FormModel) => {
      await dispatch(
        updateNotification({
          ...notification,
          additionalEmailIds: null,
          salesTeamHierarchy: null,
          maxReminderFollowups: model.maxReminderFollowups,
          reminderFrequency: model.reminderFrequency,
          reminderFrequencyType: model.reminderFrequencyType,
        }),
      )
      onClose()
    },
    [dispatch, notification, onClose],
  )

  return (
    <Formik
      validate={validate}
      initialValues={initialValues}
      onSubmit={onSubmit}
    >
      {({ submitForm, setFieldValue, values }) => (
        <SideDrawerForm onSave={submitForm} onCancel={onClose}>
          <Box p={2}>
            <NotificationLabel
              type="Customer"
              name="Agreement Reminders"
              helperText="Auto-remind customer to sign/complete agreements. All contacts will receive an 
email reminder. When a lead is disqualified, an opportunity is closed/won or closed/lost, or the customer completes all required fields, the email reminders will stop."
            />

            <Divider sx={{ mt: 2 }} />

            <Typography variant="h6" sx={{ mt: 4 }}>
              Select Number of Reminders
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              Select the maximum number of reminder emails to be sent to the
              customer
            </Typography>
            <Field
              name="maxReminderFollowups"
              component={BaseTextField}
              value={values.maxReminderFollowups}
              sx={{
                width: 260,
                mt: 0.6,
              }}
              select
            >
              {Array.from({ length: numberOfReminders }, (_, index) => (
                <MenuItem key={index + 1} value={index + 1}>
                  {index + 1}
                </MenuItem>
              ))}
            </Field>
            <Typography variant="h6" sx={{ mt: 4 }}>
              Select Reminder Frequency
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              Enter how long to wait between each email reminder
            </Typography>
            <Field
              name="reminderFrequency"
              as={TextField}
              component={BaseTextField}
              sx={{
                width: 260,
                mt: 0.6,
              }}
              inputProps={{
                min: 1,
                max: 999,
                step: 1,
              }}
              helperText="Choose a number between 1 and 999"
              type="number"
              variant="outlined"
            ></Field>

            <RadioGroup
              defaultValue="Day"
              name="reminderFrequencyType"
              value={values.reminderFrequencyType}
              onChange={(_, value) =>
                setFieldValue('reminderFrequencyType', value)
              }
            >
              <FormControlLabel
                sx={{ m: 0, mt: 1.6 }}
                value="Day"
                control={<Radio />}
                label="Days"
              />
              <FormControlLabel
                value="Week"
                sx={{ m: 0 }}
                control={<Radio />}
                label="Weeks"
              />
              <FormControlLabel
                value="Month"
                sx={{ m: 0 }}
                control={<Radio />}
                label="Months"
              />
            </RadioGroup>
          </Box>
        </SideDrawerForm>
      )}
    </Formik>
  )
}
